const SELECTOR = '.pretty-rank-boxes .rBox, .brands-detail-wrapper .list-core';
const EVENT = 'provider_listing_position';
// Stall-guard only: releases a gate if GTM is present but a tag never completes
// firing. It must be long enough NEVER to trip during a normal GA4 cold start —
// otherwise it releases the gate early and re-introduces the "last value wins"
// race (both hits report the last provider). This is not a pacing delay.
const TAG_SETTLE_STALL_GUARD_MS = 8000;

// True once the GTM container has loaded. When GTM is absent (blocked/ad-blocked)
// eventCallback will never fire, so we must not wait on it.
function gtmPresent() {
    return typeof window.google_tag_manager !== 'undefined';
}

function uniqueProviders() {
    const seen = new Set();
    const list = [];

    document.querySelectorAll(SELECTOR).forEach((card) => {
        const position = card.dataset.order || 'single';
        const serviceProvider = card.dataset.productname || 'no productname';
        const key = `${position}:${serviceProvider}`;

        if (seen.has(key)) {
            return;
        }

        seen.add(key);
        list.push({ position, serviceProvider });
    });

    return list;
}

// Push one provider_listing_position event and resolve only once GTM has
// finished firing its tags for it (eventCallback). This guarantees the GA4 tag
// has read THIS provider's position/serviceProvider from the dataLayer before
// the next push overwrites those keys — eliminating the "last value wins" race.
// GTM's DLVs persist across pushes, so without this gating the async GA4 hit
// reads whatever the last push left behind.
function pushEvent(provider) {
    return new Promise((resolve) => {
        let settled = false;
        const done = () => {
            if (settled) {
                return;
            }
            settled = true;
            resolve();
        };

        window.dataLayer = window.dataLayer || [];

        // GTM absent/blocked: nothing will ever read these keys or call
        // eventCallback, so push and move on immediately instead of waiting.
        if (!gtmPresent()) {
            window.dataLayer.push({
                event: EVENT,
                position: provider.position,
                serviceProvider: provider.serviceProvider,
            });
            setTimeout(done, 0);
            return;
        }

        window.dataLayer.push({
            event: EVENT,
            position: provider.position,
            serviceProvider: provider.serviceProvider,
            // Gate strictly on GTM finishing this event's tags. Deliberately NO
            // eventTimeout: eventTimeout makes GTM invoke eventCallback early (after
            // N ms even if the tag hasn't fired), which on a cold GA4 start releases
            // the gate before GA4 reads position/serviceProvider — the next push then
            // overwrites those shared DLV keys and every hit reports the last
            // provider (the regression seen in GTM Preview on initial load).
            eventCallback: done,
        });

        // Safety net only — trips if a tag never finishes; long enough never to
        // race GA4 warmup.
        setTimeout(done, TAG_SETTLE_STALL_GUARD_MS);
    });
}

async function sendAll(providers) {
    // Sequential on purpose: wait for each event's tags to fire before the next
    // push mutates the shared dataLayer keys.
    for (const provider of providers) {
        // eslint-disable-next-line no-await-in-loop
        await pushEvent(provider);
    }
}

function whenReady() {
    const pageLoaded = document.readyState === 'complete'
        ? Promise.resolve()
        : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));

    return pageLoaded.then(() => window.soGoogleDataFetcher?.analyticsPromise);
}

export default function init() {
    if (window.soProviderListingTracked) {
        return;
    }

    window.soProviderListingTracked = true;

    whenReady().then(() => {
        const providers = uniqueProviders();

        if (providers.length) {
            sendAll(providers);
        }
    });
}
